<script lang="ts" setup>
import { api } from '@/api';
import { DataTypeInfoDto } from '@/api/generator';
import SearchField from '@/components/filter/SearchField.vue';
import Loader from '@/components/Loader/Loader.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { translateEntityProperty } from '@/utils/helper';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { inject, onMounted, ref, Ref, watch } from 'vue';

const { t } = useTranslation();

const targetSysId = inject<Ref<string> | undefined>('targetSysId');

const headers = [
  {
    title: '',
    sortable: false,
    width: '40px',
    key: 'selected',
  },
  {
    title: `${t('Name')} | ${t('ID')}`,
    key: 'name',
    sortable: false,
  },

  {
    title: t('DataType_one'),
    key: 'type',
    sortable: false,
  },
] as const;

const combinedResults = ref<Array<DataTypeInfoDto>>();
const combinedResultsFiltered = ref<Array<DataTypeInfoDto>>();

const selectedItem = defineModel<DataTypeInfoDto | undefined>('selectedItem', {
  required: true,
});

const { action: loadDataTypeCatalog, loading } = useLoading(async () => {
  const result = await api.taskItemsApi.getDataTypeCatalogForManualTask({
    targetSystemId: targetSysId?.value || '',
  });

  if (result) {
    combinedResults.value = result.result;
    combinedResultsFiltered.value = combinedResults.value;
  }
}, true);

onMounted(loadDataTypeCatalog);

const radioGroupUpdateHandler = (value: any) => {
  selectedItem.value = value;
};

const keywordFilter = ref('');

watch(keywordFilter, (nv: string | undefined) => {
  const keyword = nv?.trim().toLowerCase();

  if (!combinedResults.value) {
    combinedResultsFiltered.value = [];
    return;
  }

  const uniqueResults = new Set<string>();

  const array = (combinedResultsFiltered.value = combinedResults.value.filter((entry) => {
    let translatedName = translateEntityProperty('name', entry)?.toLowerCase();

    if (entry.geometry && translatedName) {
      const geometry = translateEntityProperty('name', entry.geometry)?.toLowerCase();
      translatedName = `${translatedName} ${geometry}`;
    }

    if (!translatedName || (keyword && !translatedName.includes(keyword))) {
      return false;
    }

    if (uniqueResults.has(translatedName)) {
      return false;
    }

    uniqueResults.add(translatedName);
    return true;
  }));

  combinedResultsFiltered.value = array || [];
});
</script>

<template>
  <Loader v-if="loading" type="content" />
  <template v-else>
    <v-radio-group v-model="selectedItem">
      <SearchField
        v-model="keywordFilter"
        :placeholder="$t('SearchPlaceholder')"
        class="w-33 mb-4"
      />

      <OverviewTable
        :key="combinedResultsFiltered?.length || 0"
        :items="combinedResultsFiltered"
        :showItemsCount="true"
        :zebra="true"
        :noSpacing="true"
        :headers="headers"
        @click:row="
          (_event, data) => {
            radioGroupUpdateHandler(data.item);
          }
        "
      >
        <template #[`item.selected`]="{ item }">
          <v-radio :value="item"></v-radio>
        </template>
        <template #[`item.name`]="{ item }">
          <span class="font-weight-bold">
            {{ translateEntityProperty('name', item) }}
            {{ translateEntityProperty('name', item.geometry) }}
          </span>
          |
          {{ item.iuid }}
        </template>

        <template #[`item.type`]="{ item }">
          {{ item.dataTypeCategory === 'Enumeration' ? t('Enumeration_one') : t('Quantity_one') }}
        </template>
      </OverviewTable>
    </v-radio-group>
  </template>
</template>
